import { assertNotEmpty, type Compare } from '@festina-life/core'
import { useMemo } from 'react'

import { isSubTemplatedField } from '../../types/templateModel'
import { DiffPopoverLayout } from '../diffIndicators/DiffPopoverLayout'
import { stringifyComparableValue } from '../diffIndicators/helpers'
import { SubTemplateFieldValueDiffContents } from '../diffIndicators/SubTemplateFieldValueDiffContents'
import { TemplateFieldDiff } from '../diffIndicators/TemplateFieldDiff'
import { TemplateFieldDiffHeader } from '../diffIndicators/TemplateFieldDiffHeader'
import { getSpecialCasedSubTemplatedFieldValue } from './helpers'
import { SpecialCasedNestedSubTemplatedFieldDiff } from './SpecialCasedNestedSubTemplatedFieldDiff'
import type { SpecialCasedSubTemplatedValueSetup } from './types'

export const SpecialCasedSubTemplatedFieldValueDiff = ({
  current,
  compare,
}: Compare<SpecialCasedSubTemplatedValueSetup>) => {
  const areVariantsDifferentWithoutFields = useMemo(
    () =>
      stringifyComparableValue(current) !== stringifyComparableValue(compare),
    [compare, current],
  )

  const currentVariant = getSpecialCasedSubTemplatedFieldValue(current)
  const compareVariant = getSpecialCasedSubTemplatedFieldValue(compare)

  assertNotEmpty(currentVariant)

  const fields = currentVariant.fields

  return (
    <DiffPopoverLayout>
      {areVariantsDifferentWithoutFields && (
        <>
          <div className="mb-8 grid grid-cols-2 gap-x-24">
            <TemplateFieldDiffHeader
              current={currentVariant.origin}
              compare={compareVariant?.origin}
            />
          </div>
          <div className="flex flex-col gap-y-4">
            <SubTemplateFieldValueDiffContents
              current={currentVariant}
              compare={compareVariant}
            />
          </div>
        </>
      )}
      {fields.map((currentField) => {
        if (!currentField.value) {
          return null
        }

        const compareField = compareVariant?.fields.find(
          ({ name }) => name === currentField.name,
        )

        if (
          isSubTemplatedField(currentField) &&
          (isSubTemplatedField(compareField) || !compareField)
        ) {
          return (
            <SpecialCasedNestedSubTemplatedFieldDiff
              key={currentField.name}
              current={currentField}
              compare={compareField}
            />
          )
        }

        const isDifferent =
          stringifyComparableValue(currentField) !==
          stringifyComparableValue(compareField)

        if (!isDifferent) {
          return null
        }

        return (
          <TemplateFieldDiff
            key={currentField.name}
            isShowingFieldName
            current={currentField}
            compare={compareField}
          />
        )
      })}
    </DiffPopoverLayout>
  )
}
